import { ref } from "vue";
import api from "@/api";

let _state = {
  ready: false,
  abilities: new Set(),
  raw: { user: null, tree: {} },
};
let _inflight = null;

// Reactive token — Navbar watches this to re-render when permissions load
export const permissionsVersion = ref(0);

// ---------- Helpers ----------
function getCurrentUserIdFromStorage() {
  const uid =
    localStorage.getItem("user_id") ||
    (() => {
      try {
        const u = JSON.parse(localStorage.getItem("user") || "null");
        return u?.id ? String(u.id) : "";
      } catch (_) {
        return "";
      }
    })();
  return uid ? String(uid) : "";
}

function isSuperAdmin() {
  const user = _state.raw.user || null;
  if (user && (user.is_super_admin === true || user.super_admin === true)) return true;
  if (user && (user.role_id === 1 || user.id === 1)) return true;
  return false;
}

function flattenGrantedFromNav(tree) {
  const set = new Set();
  const dfs = (node) => {
    if (!node || typeof node !== "object") return;
    for (const key of Object.keys(node)) {
      const val = node[key];
      if (val && typeof val === "object" && Array.isArray(val.granted)) {
        val.granted.forEach((p) => set.add(p));
      }
      dfs(val);
    }
  };
  dfs(tree);
  return set;
}

// ---------- Getters ----------
export function loaded() {
  return _state.ready;
}

export function hasAnyPermission() {
  if (isSuperAdmin()) return true;
  return (_state.abilities?.size || 0) > 0;
}

export function has(name) {
  if (!name) return false;
  if (isSuperAdmin()) return true;
  return _state.abilities.has(name);
}

export function hasAny(names) {
  if (!names) return false;
  if (isSuperAdmin()) return true;
  const arr = Array.isArray(names) ? names : [names];
  return arr.some((n) => _state.abilities.has(n));
}

export function allPerms() {
  return _state.abilities;
}

export function me() {
  return _state.raw.user;
}

// ---------- Reset ----------
export function resetPermissions() {
  _state.ready = false;
  _state.abilities = new Set();
  _state.raw = { user: null, tree: {} };
  _inflight = null;
  permissionsVersion.value++; // trigger reactivity
  try {
    localStorage.removeItem("perm_ready");
    localStorage.removeItem("perm_granted");
    localStorage.removeItem("perm_user_id");
  } catch (_) {}
}

export function clearPermissions() {
  return resetPermissions();
}

// ---------- Init ----------
export async function initPermissions(force = false, userId = null) {
  const token = localStorage.getItem("token");
  if (!token) {
    resetPermissions();
    return;
  }

  if (_state.ready && !force) return;
  if (_inflight && !force) return _inflight;

  const currentUserId =
    userId !== null && userId !== undefined && String(userId).trim() !== ""
      ? String(userId)
      : getCurrentUserIdFromStorage();

  if (!force) {
    try {
      const ready = localStorage.getItem("perm_ready") === "1";
      const grantedRaw = localStorage.getItem("perm_granted");
      const cachedUserId = localStorage.getItem("perm_user_id") || "";
      if (ready && grantedRaw && cachedUserId && currentUserId && cachedUserId === currentUserId) {
        const arr = JSON.parse(grantedRaw);
        if (Array.isArray(arr)) {
          _state.abilities = new Set(arr);
          _state.ready = true;
          permissionsVersion.value++; // trigger reactivity
          return;
        }
      }
    } catch (_) {}
  }

  const url =
    userId !== null && userId !== undefined && String(userId).trim() !== ""
      ? `/accounting/permissions/nav/${userId}`
      : `/accounting/permissions/nav`;

  _inflight = (async () => {
    try {
      const { data } = await api.get(url);
      const user = data?.user || null;
      const tree = data?.permissions || {};

      _state.abilities = flattenGrantedFromNav(tree);
      _state.raw = { user, tree };
      _state.ready = true;
      permissionsVersion.value++; // trigger reactivity

      try {
        const uidToCache = user?.id ? String(user.id) : currentUserId;
        localStorage.setItem("perm_ready", "1");
        localStorage.setItem("perm_granted", JSON.stringify(Array.from(_state.abilities)));
        if (uidToCache) localStorage.setItem("perm_user_id", uidToCache);
        if (user?.id) localStorage.setItem("user_id", String(user.id));
        if (user?.name) localStorage.setItem("user_name", String(user.name));
      } catch (_) {}
    } catch (err) {
      resetPermissions();
      const status = err?.response?.status;
      if (status === 401) {
        const e = new Error("Unauthenticated");
        e.code = "UNAUTH";
        throw e;
      }
      throw err;
    } finally {
      _inflight = null;
    }
  })();

  return _inflight;
}

export async function loadMyPermissions(force = false, userId = null) {
  return initPermissions(force, userId);
}
